/** The inventory register: one page, four scope cards, one filterable list.
 *
 * **ADR-0035, step two.** This replaces Emissions, Scope 1 and Scope 2 — three screens that were
 * the shape of the accounting standard rather than the shape of the work. Three things follow that
 * adding screens could not have fixed:
 *
 * - **Scope 3 has somewhere to live.** It is a card that says `not assessed`, never a zero. A
 *   `0.0` beside two real figures reads as a fact and sums like one (ACC-09).
 * - **There is an "all scopes" view.** Selecting the active scope again returns to it (EXP-28),
 *   which is why the cards are toggles rather than tabs.
 * - **A figure has a row**, and a row opens a receipt.
 *
 * **The Scope 2 basis selector is ours and it is not a filter.** A filter hides rows; changing
 * basis *re-prices the same rows*. Location-based and market-based are two inventories of the same
 * electricity, the GHG Protocol requires both, and summing them double-counts — so the basis is
 * always readable and no Scope 2 figure renders without it.
 *
 * **The filters are in the URL and visible as chips** (EXP-21, EXP-12). They were `useState`, which
 * is wrong in two ways that look like nothing: opening a record from row 40 of a filtered list and
 * coming back silently un-filtered the list, and a filtered view could not be shared or bookmarked
 * at all. `register.ts` owns the selection; the chips are there because "3 filters applied" tells
 * somebody that data is hidden without telling them what, and that is the state in which people
 * conclude their figures are missing.
 */

import { CaretRight, CircleDashed } from "@phosphor-icons/react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useRef, useState } from "react";

import { ReadFailed } from "../components/Loaded";
import { PageHeader } from "../components/PageHeader";
import { Empty, Panel } from "../components/Panel";
import { InfoPopover } from "../components/ui";
import { LazyDetails } from "../components/ui/LazyDetails";
import { WindowLockBanner } from "../components/WindowLockBanner";
import { useFacilities } from "../api/evidence";
import {
  fetchSelectionExport,
  useInventory,
  useInventoryMonthly,
  useReportComparison,
} from "../api/queries";
import type { Figure } from "../api/queries";
import { sumDecimals } from "../lib/decimal";
import {
  computedQuantity,
  factorValue,
  monthLabel,
  quantity,
  share,
  shortDate,
  tonnes,
  unitLabel,
} from "../lib/format";
import { NO_RECEIPT } from "../lib/noReceipt";
import { RequireSession } from "../shell/RequireSession";
import { ScreenFrame } from "./ScreenFrame";
import styles from "./Screen.module.css";
import own from "./Inventory.module.css";
import { InventoryAnalytics, SitePanel } from "./InventoryAnalytics";
import { BandFailures } from "./bands/BandFailures";
import { MonthlyCard } from "./bands/MonthlyCard";
import { monthBreakdownKey, useMonthDrawer } from "./bands/monthBreakdown";
import { useEmissionsTrend } from "./bands/useEmissionsTrend";
import { isDrawable, modelOf } from "./monthlyModel";
import { BasisPanel, InventoryFacts, QualityPanel } from "./InventoryOverview";
import type { Fact } from "./InventoryOverview";
import { RecordPanel, RecordTabs } from "./RecordTabs";
import { ChainDots } from "./figures/ChainDots";
import { percentChange, periodName, previousWindow, priorWindowOf } from "./readinessModel";
import type { ExportContext } from "./register";
import { pagingOf } from "./drawerFigures";
import { gradeCountsOf } from "./scopeTable/model";
import { keepSearch, siteIds } from "./search";
import { SiteNarrowing } from "./SiteNarrowing";
import { siteBanner } from "./siteNarrowingModel";
import {
  REGISTER_DRAW_LIMIT,
  RENDER_CAP,
  RENDER_STEP,
  STATUSES,
  UNOWNED,
  csvFilename,
  exploreConflict,
  gradeLabel,
  toCsv,
  useRegister,
} from "./register";
import { RequirePeriod } from "./NoPeriod";
import type { Screen, Window as ScreenWindow } from "./useScreen";

const SCOPE_LABEL: Record<"1" | "2" | "3", string> = {
  "1": "Direct operations",
  "2": "Purchased energy",
  "3": "Value chain",
};

/** The three views of the inventory (journeys 3 and 5). In the URL as `tab`, so a view is a link, and
 * absent means Overview: "absent" and "the default" are one thing in one place. */
const TABS = [
  { key: "overview", label: "Overview" },
  { key: "register", label: "Register" },
  { key: "sites", label: "By site" },
] as const;
type TabKey = (typeof TABS)[number]["key"];
const isTab = (value: unknown): value is TabKey => TABS.some((entry) => entry.key === value);

/** The columns a reader may hide. The two that name and size a figure are not here. */
const COLUMNS = [
  { key: "site", label: "Site / period" },
  { key: "quantity", label: "Quantity" },
  { key: "record", label: "Record" },
  { key: "evidence", label: "Evidence" },
] as const;
type ColumnKey = (typeof COLUMNS)[number]["key"];

function cls(...parts: Array<string | false | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

/** A count of rows, grouped. Written once because this screen prints the same count in five
 * places — the panel's subtitle, two export labels, the large-register notice and the table's
 * caption — and five call sites are five chances for one of them to render `1201`. */
function count(n: number): string {
  return quantity(String(n));
}

/** Hand a file to the browser without a round trip.
 *
 * The figures are already here — the server scoped and clamped them, and narrowing by month or site
 * is a view rather than an authorization decision, so the whole window arrives in one request and
 * the selection happens in the browser. Asking the server for the same rows again to get a file
 * would be a second answer to a question already answered, and the two could differ.
 *
 * The **view** CSV is built here from these rows, and cannot be matched back to the readings behind
 * them: `FigureOut` carries no `line_id`, so it is a file to read and not a file to return. The
 * **editable** export of the same selection is a round trip to the server, which has the ids — see
 * `fetchSelectionExport`. Two files, two jobs, and each says which it is in its own label.
 */
function handOver(name: string, content: Blob | string): void {
  const blob =
    typeof content === "string" ? new Blob([content], { type: "text/csv;charset=utf-8" }) : content;
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  // In the document and removed again, and the object URL released on the next turn rather than
  // immediately: a detached anchor and a URL revoked in the same tick both work in one browser and
  // silently produce no file in another, which is a failure nobody reports as one.
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function InventoryScreenBody({ screen, period }: { screen: Screen; period: ScreenWindow }) {
  //: The editable export is a round trip, so it has the two states a round trip has: in flight, and
  //: refused. **Said on the screen rather than in the console** — a button that does nothing when
  //: pressed is the failure a reader reports as "the export is broken", and they are right.
  const [exporting, setExporting] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [exportFailed, setExportFailed] = useState<string | null>(null);
  /** The authoritative totals. **The read, not just its data**: with `inventory` undefined every
   * scope card disappears and the "All scopes" card sums an empty list to `0.0` — a total of zero
   * beside a register full of figures, produced by one dropped request (EXP-29). */
  const inventoryRead = useInventory(period, screen.method);
  const inventory = inventoryRead.data;
  const navigate = useNavigate();
  const tabSearch: { tab?: string; filing?: string; from?: string; to?: string } = useSearch({
    strict: false,
  });
  const tab: TabKey = isTab(tabSearch.tab) ? tabSearch.tab : "overview";
  //: Which legal entity a figure belongs to, by its site. `FigureOut` carries no entity and the site is
  //: the thing that belongs to one - so the entity filter the shell's context bar promised ("that
  //: narrowing belongs with the register") resolves through here.
  /** Sites, for the entity and site filters. A failed read **removed two filters silently**: the
   * entity dropdown is rendered only when there is more than one entity, and both lists are
   * derived from this - so the row shrank and nothing said why (EXP-29). */
  const facilitiesRead = useFacilities();
  const facilities = facilitiesRead.data ?? [];

  //: **The register is the server's, a page at a time.** The filters go to `GET /figures`, the options
  //: come from `GET /figures/facets`, and the rows are read as far as the reader has asked to draw
  //: (`rows`). The count of the selection and the whole-record sum are the server's for all of it, and
  //: a file is made by reading the whole selection when it is asked for. The overview reads no figure
  //: at all: its numbers are the totals read's (`/inventory`: counts, grades, the evidence summary).
  const register = useRegister({
    window: period,
    method: screen.method,
    facilities,
    active: tab === "register",
    sitesKnown: !facilitiesRead.isPending,
  });
  const { filters, options } = register;
  const figuresFailed = register.error;
  const isPending = register.isPending;
  const figuresTotal = register.total;
  const rows = register.rows;
  //: What the table puts in the document, and how many of the selection it leaves out. **Never the
  //: list anything is counted from** - see `RENDER_CAP`. A register that reported 500 records and
  //: their tonnage, of an inventory holding 4,312, would be a wrong figure introduced by a
  //: performance change with nothing on screen admitting it.
  const { shown, hidden, cap } = register.rendered;
  const scope = filters.scope;
  /** The large-register notice, which is also where focus goes after an expansion.
   *
   * **Because the control a reader just pressed can disappear under them.** "List all" removes
   * both buttons, so focus would fall to `<body>` and a keyboard reader would be returned to the
   * top of the document having just asked to look further down it - the drawer's defect (design
   * rule 10: focus comes back to something) in the one place on this screen where a control
   * unmounts itself. The paragraph is the right destination rather than a contrivance: it is the
   * sentence that states the new counts, so a listener hears the answer to what they pressed.
   */
  const notice = useRef<HTMLParagraphElement>(null);

  /** Switch view, and optionally set filters in the same navigation. One navigation, because two
   * `replace` patches in a row would each start from the URL the other had not yet written. A view
   * change is a push, so Back returns to the view the reader left. */
  const goTab = (key: string, extra: Record<string, string | undefined> = {}) => {
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => ({
        ...prev,
        ...extra,
        tab: key === "overview" ? undefined : key,
      }),
    });
  };
  /** **The sites the year-on-year bridge opened this register on.** The link names the sites and
   * the filing; the figure the banner states is summed from that filing's own comparison, so it is
   * the bridge's cell and not a number the link could misquote. The read is issued only when sites
   * were asked for. */
  const sitesAsked = register.askedSites;
  const comparisonRead = useReportComparison(
    sitesAsked.length > 0 ? tabSearch.filing : undefined,
    screen.method,
  );
  const siteNames = new Map<string, string>([
    ...facilities.map((facility): [string, string] => [facility.id, facility.name]),
    ...(comparisonRead.data?.movements ?? []).map((movement): [string, string] => [
      movement.facility_id,
      movement.facility_name,
    ]),
  ]);
  const banner =
    sitesAsked.length > 0
      ? siteBanner({
          asked: sitesAsked,
          shown: siteIds(register.filters.sites),
          names: siteNames,
          comparison: comparisonRead.data,
          from: tabSearch.from,
          to: tabSearch.to,
        })
      : undefined;
  const showWindow = (from: string, to: string) => {
    void navigate({ to: ".", search: (prev: Record<string, unknown>) => ({ ...prev, from, to }) });
  };
  const [hiddenCols, setHiddenCols] = useState<ReadonlySet<ColumnKey>>(() => new Set());

  /** **The prior period, read only for the Overview and only once this one has figures.** A comparison
   * against nothing is no comparison, and the register tab should not pay for a second inventory read
   * it does not draw. Held or derived as the home screen does it (`priorWindowOf`, `previousWindow`);
   * with neither, no request is made and the tile is absent, never zero. */
  const priorWindow =
    tab === "overview" ? (priorWindowOf(period, screen.windows) ?? previousWindow(period)) : undefined;

  const cards = inventory?.totals_by_scope ?? [];
  //: How many figures the window holds, as the totals read counted them: the sum of the cards'
  //: counts. Never the length of a list.
  const windowFigures = cards.reduce((n, card) => n + card.figures, 0);
  const comparing = priorWindow && !inventoryRead.error && windowFigures > 0 ? priorWindow : undefined;
  const priorInventory = useInventory(comparing, screen.method);

  /** **The traceability summary, asked for only on the Overview.** The six-link assessment of every
   * figure in the window, reduced to a count and the commonest break by the server: the facts below
   * state it without loading a figure. */
  const evidenceRead = useInventory(tab === "overview" ? period : undefined, screen.method, {
    evidence: true,
  });

  /** **The monthly series, read only for the Overview** (ADR-0110). Same window and same Scope 2
   * method as the totals above it, so a switch of either re-reads both. No window, no request. */
  const monthlyRead = useInventoryMonthly(tab === "overview" ? period : undefined, screen.method);

  const hasAssessedScopes = cards.some((card) => card.assessed);
  /** The combined card's figure, and the denominator each scope's share is drawn against. The
   * server's own sums over the scopes it says are assessed, added once here for the share and for
   * the card, so the two cannot disagree. A share is presentation (rule 5); nothing is rounded
   * before it. */
  const assessedTotal = inventory?.total_kgco2e ?? "0";
  // **The register's total is the server's**, over the whole selection as whole records (a record
  // that straddles the window counts in full, unlike the cards above). The page states it with the
  // count; this screen adds nothing up. Absent from a response that does not state it: no figure,
  // never a sum of the rows that happen to be loaded.
  const total = register.wholeRecordKg;
  const filtered = register.isFiltered;
  const explorationConflict = (key: "scope" | "site", selected: string): string | undefined =>
    inventoryRead.isPending || inventoryRead.error || facilitiesRead.isPending || facilitiesRead.error
      ? "Read the source figures and sites successfully before exploring."
      : exploreConflict({
          key,
          selected,
          filters,
          facets: register.facets,
          entities: register.entities,
          chips: register.chips,
        });

  /** The total the months must add up to: the cards' own figure, summed exactly over the scopes
   * the server says are assessed. Absent while the totals are not read, so the chart never claims
   * to reconcile with a number it has not seen. */
  const monthlyExpectedKg = inventory
    ? sumDecimals(cards.filter((card) => card.assessed).map((card) => card.kgco2e))
    : undefined;
  const monthlyModel = monthlyRead.data ? modelOf(monthlyRead.data, monthlyExpectedKg) : undefined;
  /** **The monthly chart is the band's** (design uplift U5): the same card, the same per-scope "still
   * arriving" rule and the same click as Home and the Executive dashboard. A month opens the Every
   * figure drawer on that month, with the period untouched. The reads are the band's own (the months
   * and the census), asked only for the Overview, and the months must still add up to the cards'
   * total or nothing is drawn. */
  const trend = useEmissionsTrend(
    { window: tab === "overview" ? period : undefined },
    screen.method,
    cards.filter((card) => card.assessed).map((card) => card.scope as "1" | "2" | "3"),
    undefined,
    { trends: false },
  );
  const unreconciled =
    trend.unreconciled ??
    (monthlyModel && !monthlyModel.reconciliation.ok
      ? (monthlyModel.reconciliation.reason ?? "The months do not add up.")
      : undefined);
  const monthDrawer = useMonthDrawer({
    key: screen.breakdown,
    window: period,
    method: screen.method,
    months: monthlyRead.data?.months,
    label: "Every figure",
  });

  const facts: Fact[] = [];
  const gradesInWindow = gradeCountsOf(inventory);
  if (comparing && priorInventory.data && hasAssessedScopes) {
    const priorCards = priorInventory.data.totals_by_scope;
    // The same scopes assessed on both sides, or the arrow is between two methods.
    const sameScopes = cards.every(
      (card) => priorCards.find((c) => c.scope === card.scope)?.assessed === card.assessed,
    );
    const priorTotal = priorInventory.data.total_kgco2e;
    const change = sameScopes ? percentChange(Number(assessedTotal), Number(priorTotal)) : undefined;
    if (change) {
      facts.push({
        key: "inv-prior",
        label: `Prior period · ${periodName(comparing)}`,
        value: tonnes(priorTotal),
        unit: "tCO₂e",
        note: "Allocated totals; the same scopes are assessed in both periods",
        delta: { ...change, versus: periodName(comparing) },
      });
    }
  }
  //: **The facts are the totals read's**, not a tally of loaded figures: the count of figures is the
  //: cards' (`figures`), the metered count is their `graded_a`, and the evidence chain is the
  //: server's six-link assessment of every figure (`evidence`). Each is over the whole window however
  //: many figures it holds, and none is stated while its read is pending or has failed.
  const evidence = evidenceRead.data?.evidence;
  const gradeA = cards.reduce((n, card) => n + card.graded_a, 0);
  if (inventory && !inventoryRead.error && windowFigures > 0) {
    facts.push({
      key: "inv-figures",
      label: "Source figures",
      value: count(windowFigures),
      note: "Whole records that overlap this window",
      drill: { label: "Open the register", onOpen: () => goTab("register") },
    });
    if (evidence && evidence.figures > 0) {
      const incomplete = evidence.untraceable;
      facts.push({
        key: "inv-evidence",
        label: "Evidence chain complete",
        value: count(evidence.figures - incomplete),
        unit: `of ${count(evidence.figures)}`,
        note:
          incomplete === 0
            ? "Every link from figure to source holds"
            : `${count(incomplete)} not complete`,
        drill:
          incomplete > 0
            ? { label: "Show the incomplete", onOpen: () => goTab("register", { status: "broken" }) }
            : undefined,
      });
    }
    facts.push({
      key: "inv-metered",
      label: "Metered (grade A)",
      value: count(gradeA),
      unit: `of ${count(windowFigures)}`,
      drill:
        gradeA > 0
          ? { label: "Show grade A", onOpen: () => goTab("register", { quality: "A" }) }
          : undefined,
    });
  }

  const exportContext: ExportContext = {
    entity: screen.organization?.legal_name ?? "",
    period_start: period.period_start,
    period_end: period.period_end,
    method: screen.method,
    chips: register.chips,
    of: windowFigures,
  };

  return (
    <ScreenFrame
      screen={screen}
      title="All emissions"
      crumb="every figure, by scope and period"
      {...(monthDrawer ??
        (tab === "register"
          ? {
              breakdownFigures: rows,
              breakdownLabel: "Filtered inventory",
              breakdownRead: register.read,
              breakdownPaging: pagingOf(register.read),
            }
          : {}))}
      // **The body scrolls** (UX-03's mechanism, Reports.tsx's precedent).
      //
      // Without it every panel is a fixed grid row and anything taller is CLIPPED — which is what
      // happened to the sector-methodologies panel: 1,592px of content in a box showing a title
      // and nothing else. `ui-check` passed throughout, because `innerText` returns clipped text,
      // so the gate read six thousand characters a user could not see. That is the sharpest
      // version of this project's recurring failure: a check that confirms the data is there
      // rather than that somebody can read it.
      bodyScrolls
    >
      <PageHeader
        actions={<InfoPopover infoKey="legacy.emissions-all" />}
        tabs={
          <RecordTabs
            tabs={TABS}
            tab={tab}
            onSelect={(key) => goTab(key)}
            label="Inventory views"
            saying={`Showing ${TABS.find((entry) => entry.key === tab)?.label ?? "Overview"}`}
          />
        }
      />

      <section className={own.overview} aria-label="Inventory by scope" data-testid="inventory-scope-kpis"
        data-state={inventoryRead.error ? "failed" : inventoryRead.isPending || !inventory ? "loading" : cards.length === 0 ? "empty" : "loaded"}>
        <h2 className="visuallyHidden">Explore by scope</h2>
        {/* Whether the window these totals are for is closed (ADR-0094 D2). From the totals'
            own response, so it cannot disagree with the cards beneath it; nothing while that
            read is pending or failed, which is not a claim that the window is open. */}
        <WindowLockBanner lock={inventory?.window_lock} />
        {/* **A failed totals read is not a zero total.** The cards are the server's own sums, and
            with none of them the combined card renders `0.0 tCO₂e` over a register that is full —
            which is the worst direction this product can be wrong in. */}
        {inventoryRead.error ? (
          <ReadFailed
            of="The authoritative totals"
            error={inventoryRead.error}
            onRetry={() => void inventoryRead.refetch()}
          />
        ) : inventoryRead.isPending || !inventory ? (
          <div role="status" aria-busy="true" className={own.cards} data-testid="inventory-totals-loading">
            <span className="visuallyHidden">Reading inventory totals</span>
            {["All scopes", "Scope 1", "Scope 2", "Scope 3"].map((label) => (
              <div className={own.cardSkeleton} key={label} aria-hidden="true">
                <span className={own.cardName}>{label}</span>
                <span className={own.skeletonValue} />
                <span className={own.skeletonNote} />
              </div>
            ))}
          </div>
        ) : cards.length === 0 ? (
          <Empty>No inventory totals are available for this reporting window.</Empty>
        ) : (
        <div className={cls(own.cards)} role="group" aria-label="Scope">
          <button
            type="button"
            className={cls(own.card, scope === "" && own.cardOn)}
            aria-pressed={scope === ""}
            onClick={() => register.clear("scope")}
          >
            <span className={cls(own.cardName)}>All scopes</span>
            <span className={cls(own.cardSub)}>{cards.every((card) => card.assessed) ? "Reporting-window total" : "Assessed scopes only"}</span>
            {/* **The server's sum, not the browser's.** An earlier version added the rows up
                here, which is how the superseded-figure defect stayed invisible: the card and the
                list agreed *because both were wrong*. Authoritative totals arrive from the
                server; the formatter renders them and recalculates nothing. */}
            {hasAssessedScopes ? <span className={cls(own.cardRow, "num")}>
              <span className={cls(own.cardValue)}>{tonnes(assessedTotal)}</span>
              <span className={cls(own.cardUnit)}>tCO₂e</span>
            </span> : <span className={own.cardUnassessed}>Not assessed</span>}
            <span className={own.cardAction}>Show all scopes</span>
          </button>

          {cards.map((card) => (
            <button
              key={card.scope}
              type="button"
              className={cls(own.card, scope === card.scope && own.cardOn)}
              aria-pressed={scope === card.scope}
              // **EXP-28**: selecting the active scope again restores All scopes. A toggle, not a
              // tab — a tab set with no way back is the defect the requirement names.
              onClick={() => register.set("scope", scope === card.scope ? "" : card.scope)}
            >
              <span className={cls(own.cardName)}>Scope {card.scope}</span>
              <span className={cls(own.cardSub)}>
                {SCOPE_LABEL[card.scope as "1" | "2" | "3"]}
              </span>
              {/* **Not a zero.** An unassessed scope is a question nobody has answered, and
                  rendering `0.0` beside two real figures reads as a fact and sums like one. */}
              {card.assessed ? (
                <>
                  <span className={cls(own.cardRow, "num")}>
                    <span className={cls(own.cardValue)}>{tonnes(card.kgco2e)}</span>
                    <span className={cls(own.cardUnit)}>tCO₂e</span>
                    <span className={cls(own.cardShare)}>
                      {share(card.kgco2e, assessedTotal) ?? ""}
                    </span>
                  </span>
                  <span className={cls(own.cardBar)} aria-hidden="true">
                    <span
                      className={cls(
                        own.cardFill,
                        card.scope === "2" ? own.cardFill2 : card.scope === "3" ? own.cardFill3 : own.cardFill1,
                      )}
                      style={{
                        width: `${Math.max(
                          0,
                          Math.min(100, (Number(card.kgco2e) / (Number(assessedTotal) || 1)) * 100),
                        )}%`,
                      }}
                    />
                  </span>
                </>
              ) : (
                <>
                  {/* **Scope 3 is fifteen questions, not one** (S3-01, verification log S-08). A
                      single "not assessed" reports the same thing whether fourteen categories were
                      answered or none were, and the standard requires every category to be
                      accounted for with any exclusion justified. Never a figure: a `0.0` beside
                      two real scopes reads as a fact and sums like one. */}
                  <span className={cls(own.cardUnassessed)}>
                    <CircleDashed size={16} weight="regular" aria-hidden="true" />
                    {card.scope === "3" && inventory?.scope3
                      ? `${inventory.scope3.assessed} of 15 assessed`
                      : "Not assessed"}
                  </span>
                  <span className={cls(own.cardUnit)}>
                    {card.scope === "3" && inventory?.scope3
                      ? inventory.scope3.is_complete
                        ? "all categories accounted for"
                        : `${inventory.scope3.not_assessed.length} categories not looked at`
                      : "no assessment total available"}
                  </span>
                </>
              )}
              <span className={own.cardAction}>
                {scope === card.scope ? "Show all scopes" : `Explore Scope ${card.scope}`}
              </span>
            </button>
          ))}
        </div>
        )}

      </section>

        {/* **A failed site read is said on every tab, not shown as two missing dropdowns.** Both the
          entity and site filters, and the names on the chips, are derived from the facility list, so a
          dropped request narrowed the control row and left a reader looking for a filter that was
          there yesterday. On the Overview there is no control row to lose them from, which is why it
          is here and not beside the controls. */}
      {register.optionsError && tab !== "register" ? (
        <ReadFailed
          of="The register's filters"
          error={register.optionsError}
          onRetry={register.retryOptions}
        />
      ) : null}
      {facilitiesRead.error ? (
        <p className={cls(own.filterFailed, own.fullRow)} role="alert" data-testid="site-filter-failed">
          The site and entity filters could not be loaded.{" "}
          <button
            type="button"
            className={styles.fixLink}
            onClick={() => void facilitiesRead.refetch()}
          >
            Try again
          </button>
        </p>
      ) : null}

      {/* **What is in force, in words, with a way out of each one** (EXP-21). The chips sit above
          the tabs rather than inside the register's controls, so they are in view on every tab: a
          `<select>` showing its own value only answers the question of somebody already looking at
          that dropdown — and the one filter people forget is the one they set on the previous
          screen and carried here. */}
        {register.chips.length > 0 ? (
          <div className={cls(own.chips)} data-testid="filter-context">
            <span className={cls(own.chipsLead)}>Showing</span>
            {register.chips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                className={cls(own.chip)}
                onClick={() => register.clear(chip.key)}
              >
                <span className={cls(own.chipLabel)}>{chip.label}</span>
                {chip.value}
                {/* A word, not only a glyph: the control has to be announced as what it does. */}
                <span className={cls(own.chipX)} aria-hidden="true">
                  ×
                </span>
                <span className={cls(own.chipHidden)}>— clear this filter</span>
              </button>
            ))}
            <button type="button" className={styles.fixLink} onClick={register.clearAll}>
              Clear all filters
            </button>
            {tab !== "register" ? (
              <button type="button" className={styles.fixLink} onClick={() => goTab("register")}>
                Open the register
              </button>
            ) : null}
          </div>
        ) : null}


      {banner ? (
        <SiteNarrowing
          banner={banner}
          comparison={
            !tabSearch.filing
              ? "pending"
              : comparisonRead.error
                ? "failed"
                : comparisonRead.data
                  ? "ok"
                  : "pending"
          }
          figures={figuresFailed ? "failed" : isPending ? "reading" : "ready"}
          onShowEvery={() => register.clear("sites")}
          onShowWindow={showWindow}
          onRetry={() => void comparisonRead.refetch()}
        />
      ) : null}

      <div className={own.tabArea}>
        <RecordPanel tab={tab}>
          {tab === "overview" ? (
          <div className={own.stack}>
            <InventoryFacts facts={facts} />
            <BandFailures
              reads={[
                { of: "The monthly figures", read: trend.monthly },
                { of: "The expected bills", read: trend.coverage },
              ]}
            />
            <MonthlyCard
              scopes={cards.filter((card) => card.assessed).map((card) => card.scope as "1" | "2" | "3")}
              bars={unreconciled ? undefined : trend.bars}
              reads={trend.reads}
              onOpenMonth={(month) => screen.openBreakdown(monthBreakdownKey(month))}
              unreconciled={unreconciled}
              estimates={trend.estimates}
            />
            {inventory && !inventoryRead.error && !inventoryRead.isPending && cards.length > 0 ? (
              <InventoryAnalytics inventory={inventory}
                monthlyShown={isDrawable(monthlyModel)}
                scopeConflict={(selected) => explorationConflict("scope", selected)}
                siteConflict={(selected) => explorationConflict("site", selected)}
                onOpenScope={(selected) => { if (!explorationConflict("scope", selected)) register.set("scope", selected); }}
                onOpenSite={(selected) => { if (!explorationConflict("site", selected)) register.set("site", selected); }} />
            ) : null}

            {inventoryRead.error ? (
              <Panel title="Data quality" sub="Source figures by grade">
                <ReadFailed
                  of="The figures for this period"
                  error={inventoryRead.error}
                  onRetry={() => void inventoryRead.refetch()}
                />
              </Panel>
            ) : gradesInWindow === undefined ? (
              <Panel title="Data quality" sub="Source figures by grade">
                <Empty>Reading this period&rsquo;s figures…</Empty>
              </Panel>
            ) : (
              <QualityPanel
                grades={gradesInWindow}
                onOpenGrade={(grade) => goTab("register", { quality: grade })}
              />
            )}

            {inventory && !inventoryRead.error ? (
              <BasisPanel inventory={inventory} scope={scope} method={screen.method} />
            ) : null}

          {/* **S3-04 gets its own panel, not a footnote under the cards.**

              It was inside the scope-card panel first, and that panel's grid row has a fixed height —
              so the content overflowed it and `ui-check` could no longer click the Scope 1 card. The
              browser gate caught a layout defect a unit test cannot see, which is what it is for.

              Its own panel is also the right shape: this is not an annotation on a figure, it is the
              answer to "why is there no figure", and it is as long as the four families need. */}
          {(scope === "" || scope === "3") && inventory?.scope3?.method_packs?.length ? (
            <Panel
              title="Sector methodologies"
              sub="What a financed, use-phase, process or specialist figure would need"
              column="1 / 13"
            >
            <LazyDetails
              className={own.disclosure}
              summary={<><CaretRight size={16} weight="regular" aria-hidden="true" />View methodology limitations</>}
            >
            {/* **S3-04: an unsupported asset class displays a limitation, not a zero.** Shown where
                somebody is already asking why Scope 3 has no number — a separate screen would be
                found by the people who did not need it.

                It is prose and not a grid because a table with a dash in every cell reads as data
                that failed to load, and because the limitations are not interchangeable: two of
                category 15's seven allocate on different denominators and one is reported outside
                scope 3 altogether, which a column of dashes cannot say. */}
            <div className={cls(own.packs)} data-testid="method-packs">
              <p className={cls(own.packNeeds)}>
                Sector methodologies are <strong>not</strong> available in EmitIQ. Each needs inputs
                this product does not hold, so no figure is produced for any of them, and none is
                estimated from spend or from fuel, because an approximation would complete and
                nothing on the figure could show how far out it was.
              </p>
              {inventory.scope3.method_packs.map((pack) => (
                <section key={pack.key} className={cls(own.pack)}>
                  <p className={cls(own.packName)}>
                    {pack.label}
                    {pack.supported ? null : (
                      <>
                        {" "}
                        <span className={cls(own.packTag)}>No method</span>
                      </>
                    )}
                  </p>
                  <p className={cls(own.packNeeds)}>{pack.prerequisite}</p>
                  <ul className={cls(own.packClasses)}>
                    {pack.classes.map((item) => (
                      <li key={item.key} className={cls(own.packClass)}>
                        <span className={cls(own.packClassHead)}>
                          <span className={cls(own.packClassName)}>{item.name}</span>
                          {/* Required, optional and reported-separately are three different answers
                              to "does this affect my filing". One grey tag for all three would
                              overstate the optional lines and understate the required ones. */}
                          <span
                            className={cls(
                              own.packTag,
                              item.obligation === "required" ? own.packTagRequired : "",
                            )}
                          >
                            {item.obligation.replace(/_/g, " ")}
                          </span>
                        </span>
                        <span className={cls(own.packClassBody)}>
                          {item.limitation}
                          <a
                            className={cls(own.packCite)}
                            href={item.source_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {item.citation}
                          </a>
                        </span>
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
            </LazyDetails>
            </Panel>
          ) : null}
          </div>
          ) : tab === "sites" ? (
          <div className={own.stack}>
            {inventory && !inventoryRead.error && !inventoryRead.isPending && cards.length > 0 ? (
              <section aria-label="Inventory by site" data-testid="inventory-sites">
                <SitePanel
                  inventory={inventory}
                  siteConflict={(selected) => explorationConflict("site", selected)}
                  onOpenSite={(selected) => {
                    if (!explorationConflict("site", selected)) register.set("site", selected);
                  }}
                />
              </section>
            ) : inventoryRead.error ? (
              <ReadFailed
                of="The authoritative totals"
                error={inventoryRead.error}
                onRetry={() => void inventoryRead.refetch()}
              />
            ) : (
              <Empty>Reading the site totals…</Empty>
            )}
          </div>
          ) : (
          <Panel
            title={scope === "" ? "All scopes inventory" : `Scope ${scope} inventory`}
            // **Separated, like every other count on this screen.** `1201 records` beside a notice
            // reading `1,201 figures` is one number rendered two ways on one panel, and a reader
            // checks whether they are the same number instead of reading the figures. Rounding and
            // grouping are the presentation layer's (rule 5), and this is one.
            {...(total === undefined || figuresFailed || isPending ? {} : { subNoReceipt: NO_RECEIPT.sumOfRows })}
            sub={
              figuresFailed
                ? "The figures could not be read"
                : isPending
                  ? "Reading this period's figures"
                  : [
                      `${count(figuresTotal)} record${figuresTotal === 1 ? "" : "s"}`,
                      ...(total === undefined ? [] : [`${tonnes(total)} tCO₂e`]),
                      "whole-record values",
                      ...(filtered ? ["filtered"] : []),
                    ].join(" · ")
            }
            action={
              !figuresFailed && !isPending && figuresTotal > 0 ? (
                /* **Each export says what it will contain, in its own label.** "Export" beside a
                   filtered table is the control that produces a file named after an inventory and
                   holding a twelfth of one. The context goes into the file too — see `toCsv` and the
                   workbook's "About this file" sheet.

                   **Two controls, because they are two files.** The CSV is a view: it carries the
                   emissions, the quality and where each receipt breaks, and it cannot be sent back
                   because a figure has no `line_id`. The workbook is the editable inventory — the
                   readings, with the two ids a correction needs — and it is the same selection, which
                   is EXP-21's filtered-export clause. One control labelled "Export" would make the
                   reader guess which they were getting, and the wrong guess is a file they edit and
                   cannot return. */
                <div className={cls(own.exports)}>
                  <button
                    type="button"
                    className={cls(own.export)}
                    disabled={csvBusy}
                    onClick={() => {
                      setExportFailed(null);
                      setCsvBusy(true);
                      // **The whole selection, read now.** The register holds the pages it has drawn,
                      // and a file named after an inventory that carried those would be a different
                      // answer to the question the label asks.
                      void register
                        .readSelection()
                        .then((all) =>
                          handOver(csvFilename(exportContext, all), toCsv(all, exportContext, register.entities)),
                        )
                        .catch((failure: unknown) =>
                          setExportFailed(
                            failure instanceof Error ? failure.message : "That export could not be produced.",
                          ),
                        )
                        .finally(() => setCsvBusy(false));
                    }}
                  >
                    {csvBusy
                      ? "Preparing the file…"
                      : `Export ${filtered ? `these ${count(figuresTotal)}` : `all ${count(figuresTotal)}`} as CSV`}
                  </button>
                  <button
                    type="button"
                    className={cls(own.export)}
                    data-testid="export-editable"
                    disabled={exporting}
                    onClick={() => {
                      setExportFailed(null);
                      setExporting(true);
                      // **The ids of the rows in force, from the selection and never from `shown`.**
                      // The table renders at most `RENDER_CAP` of them, and a file holding what the
                      // table happened to draw would be a different answer to the same question — the
                      // bug `register.rendered` exists to keep out of every count on this screen. The
                      // whole selection is read from the server now.
                      void register
                        .readSelection()
                        .then((all) =>
                          fetchSelectionExport(period, all.map((figure) => figure.activity_record_id), "xlsx"),
                        )
                        .then(({ blob, filename }) => handOver(filename, blob))
                        .catch((failure: unknown) =>
                          setExportFailed(
                            failure instanceof Error
                              ? failure.message
                              : "That export could not be produced.",
                          ),
                        )
                        .finally(() => setExporting(false));
                    }}
                  >
                    {exporting
                      ? "Preparing the workbook…"
                      : `Export ${filtered ? `these ${count(figuresTotal)}` : `all ${count(figuresTotal)}`} for editing`}
                  </button>
                </div>
              ) : null
            }
          >
            {/* **A refused export says so here, where the button is** (UX-04, EXP-29). A control that
                does nothing when pressed is the failure a reader reports as "the export is broken",
                and they are right to. The sentence comes from the response rather than from a status
                code: a 422 means they filtered to nothing, which is a different thing to fix. */
            exportFailed ? (
              <p className={styles.note} role="alert" data-testid="export-failed">
                {exportFailed}
              </p>
            ) : null}

            <div className={cls(own.filters)}>
              <input
                className={cls(own.search)}
                aria-label="Search activity, site or carrier"
                placeholder="Search activity, site or carrier…"
                value={filters.q}
                onChange={(event) => register.set("q", event.target.value)}
              />
              {/* **A column menu**, because the register is where the dense work happens and a reader
                  comparing quantities does not want the evidence column, nor an auditor the quantity. The
                  two columns that name and size a figure (activity, tCO₂e) are not offered: a row with no
                  name and no value is not a figure. Held in the page, not the URL - it is a preference
                  about this reader's view and selects nothing, so it must never become a chip. */}
              <details className={cls(own.colMenu)} data-testid="column-menu">
                <summary>Columns</summary>
                <div className={cls(own.colList)} role="group" aria-label="Visible columns">
                  {COLUMNS.map((column) => (
                    <label key={column.key} className={cls(own.colItem)}>
                      <input
                        type="checkbox"
                        checked={!hiddenCols.has(column.key)}
                        onChange={() =>
                          setHiddenCols((now) => {
                            const next = new Set(now);
                            if (next.has(column.key)) next.delete(column.key);
                            else next.add(column.key);
                            return next;
                          })
                        }
                      />
                      {column.label}
                    </label>
                  ))}
                </div>
              </details>
              <select
                aria-label="Month"
                value={filters.month}
                onChange={(event) => register.set("month", event.target.value)}
              >
                <option value="">All months</option>
                {/* **A month is a name** (EXP-39). `2025-04` is ISO and so not ambiguous, but it is a
                    value a reader has to decode in a control they are choosing from — and the rule
                    that no date this product shows is written in digits alone is easier to keep than
                    one with an exception in it. */}
                {options.months.map((month) => (
                  <option key={month} value={month}>
                    {monthLabel(month)}
                  </option>
                ))}
              </select>
              <select
                aria-label="Accounting category"
                value={filters.category}
                onChange={(event) => register.set("category", event.target.value)}
              >
                <option value="">All categories</option>
                {options.categories.map((category) => (
                  <option key={category} value={category}>
                    {category.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
              <select
                aria-label="Source type"
                value={filters.source}
                onChange={(event) => register.set("source", event.target.value)}
              >
                <option value="">All source types</option>
                {options.sources.map((source) => (
                  <option key={source} value={source}>
                    {source.replace(/_/g, " ")}
                  </option>
                ))}
              </select>
              {/* **Entity above site**, and only when there is more than one. A single-entity
                  workspace shown an entity dropdown is shown a control that cannot change anything,
                  which is how a reader learns to ignore the row. */}
              {options.entities.length > 1 ? (
                <select
                  aria-label="Legal entity"
                  value={filters.entity}
                  onChange={(event) => register.set("entity", event.target.value)}
                >
                  <option value="">All entities</option>
                  {options.entities.map((entity) => (
                    <option key={entity} value={entity}>
                      {entity.replace(" (SAMPLE DATA)", "")}
                    </option>
                  ))}
                </select>
              ) : null}
              <select
                aria-label="Site"
                value={filters.site}
                onChange={(event) => register.set("site", event.target.value)}
              >
                <option value="">All sites</option>
                {options.sites.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
              {/* The evidence chain, which is the only status a figure has — `AWAITING_REVIEW` is the
                  only route into `VERIFIED` and a figure exists only once a verified record was
                  promoted, so a workflow filter here would have one value. Documents still in review
                  are the evidence queue's list. */}
              <select
                aria-label="Evidence status"
                value={filters.status}
                onChange={(event) => register.set("status", event.target.value)}
              >
                <option value="">Any evidence state</option>
                {STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              {/* **Data quality — the grade the server derived** (ADR-0036), offered as the four it can
                  be. It exists because the home screen's *Metered* number is a count of grade A, and
                  a link behind a share that opened every figure was a claim nobody could check. */}
              <select
                aria-label="Data quality grade"
                value={filters.quality}
                onChange={(event) => register.set("quality", event.target.value)}
              >
                <option value="">Any grade</option>
                {options.qualities.map((grade) => (
                  <option key={grade} value={grade}>
                    {gradeLabel(grade)}
                  </option>
                ))}
              </select>
              {/* **Owner — the sixth dimension, and it answers a question rather than a column**
                  (EXP-21, decision **M-08**, 2026-10-12).
                  A figure inherits the owner of the expected source it arrived through. Nothing is
                  stored on the measurement, so this is not "who created it" — that is authorship, it
                  is already in the ledger, and it is history. An owner answers for the figure *going
                  forward*, which is why a bulk-applied figure with no meaningful author still has one.
                  "Nobody" is offered unconditionally, because "show me what nobody answers for" is a
                  real question even when the answer is none — and an empty result there is
                  information, not a dropped filter. */}
              <select
                aria-label="Owner"
                value={filters.owner}
                onChange={(event) => register.set("owner", event.target.value)}
              >
                <option value="">Any owner</option>
                <option value={UNOWNED}>Nobody answers for it</option>
                {options.owners.map(([id, name]) => (
                  <option key={id} value={id}>
                    {name}
                  </option>
                ))}
              </select>
            </div>

            {/* **What the owner means, kept beside the control that uses it.**
                This paragraph used to say *"There is no owner filter, because no figure has an owner"*,
                and it was true: the clause was open as **M-08** until KD answered it on 2026-10-12. The
                honest half of it survives and is the sentence below — the owner is not a property of
                the measurement, it is inherited from the stream the figure arrived through — because
                that is what tells a reader where to go to change it.
                Three outcomes, and the filter collapses two of them while the row keeps all three: the
                figure has an owner; it arrived through a declared stream nobody is responsible for; or
                it matched no declared stream at all. The last two look identical in a list and have
                completely different remedies — assign somebody, or declare the source — so a figure
                with no expectation behind it is never rendered as merely "unassigned". */}
            {/* **How to read this register, as one disclosure** (honesty copy ranked, not removed): why
                its sums can differ from the totals above, and where an owner comes from. Closed by
                default; every sentence that was a paragraph is in it. */}
            <LazyDetails
              className={cls(own.disclosure, own.reading)}
              testId="register-reading"
              summary={
                <>
                  <CaretRight size={16} weight="regular" aria-hidden="true" />
                  How to read this register
                </>
              }
            >
              <p className={own.registerContext}>
              This register shows whole records that overlap the window. Inventory totals above allocate
              those records to calendar days, so the sums can differ. Filters narrow this register,
              not the inventory overview.
              </p>
              <p className={cls(own.ownerNote)} data-testid="owner-basis">
              An owner is inherited from the{" "}
              <Link to="/sources" search={keepSearch}>
                expected source
              </Link>{" "}
              a figure arrived through, not recorded on the measurement, so changing it is a
              change to who collects that stream. Who <em>accepted</em> a figure is a different
              question and is on its receipt; the work assigned to a person is on{" "}
              <Link to="/tasks" search={keepSearch}>
                tasks and requests
              </Link>
              , and a record&rsquo;s own tasks are on its <em>Workflow</em> tab.
              </p>
            </LazyDetails>

            {/* **The result of a filter change, announced** (EXP-39).
                Every control on this screen is a `<select>` or a text box whose only effect is that
                the table underneath becomes a different table. A sighted reader sees that happen; a
                screen reader says nothing at all, because replacing rows is not an event — so somebody
                working without the screen chose a site and got silence, with no way to tell a filter
                that matched 3 figures from one that matched none.

                **In the document from the first render, never conditional.** A live region inserted at
                the moment it has something to say is frequently not announced: the assistive layer has
                to have been watching the node before its contents changed. That is why this renders an
                empty string rather than `null` while the figures load, and why it is a sibling of the
                table rather than inside the branch that chooses between the table and an empty state.

                `polite`, not `assertive`: it must not interrupt the reader mid-word on each keystroke
                of the search box.

                **"narrowed by" and "no filters in force", never the word "filtered".** `ui-check`
                asserts that returning from a record lands on a list that still says it is filtered, by
                searching the screen's text for `/filtered/i` — and "unfiltered" matches that, so a
                hidden region using the obvious word would have made an existing gate pass
                unconditionally. A string here is not only read by people. */}
            <p className="visuallyHidden" role="status" aria-live="polite" data-testid="register-live">
              {isPending || figuresFailed
                ? ""
                : `${figuresTotal} ${figuresTotal === 1 ? "figure" : "figures"}${
                    total === undefined ? "" : `, ${tonnes(total)} tonnes CO2 equivalent`
                  }${
                    register.chips.length > 0
                      ? `, narrowed by ${register.chips.map((chip) => chip.label.toLowerCase()).join(" and ")}`
                      : ", no filters in force"
                  }${
                    hidden > 0
                      ? `. ${shown.length} of them are drawn, and ${hidden} more can be drawn below`
                      : ""
                  }`}
            </p>

            {!isPending && !figuresFailed && windowFigures > 0 ? (
              <p className={cls(own.countLine)} data-testid="register-count">
                <strong className="num">{count(figuresTotal)}</strong> of{" "}
                <span className="num">{count(windowFigures)}</span> figures
              </p>
            ) : null}

            {figuresFailed ? (
              /* **A failed request is not an empty inventory.** This screen used to drop the error and
                 render "No figures for this period yet", which reports a failure as a successful
                 answer — and in this product an inventory that reads as empty is the one mistake
                 nobody double-checks. The retry is here because the alternative offered to the reader
                 was reloading the page. */
              <ReadFailed
                of="The figures for this period"
                error={figuresFailed}
                onRetry={register.retry}
              />
            ) : isPending ? (
              <Empty>Loading…</Empty>
            ) : figuresTotal === 0 ? (
              /* An empty *filtered* result explains the filters and offers a way back — it is a
                 different sentence from "there is nothing here", and conflating them sends somebody
                 looking for missing data that is one dropdown away. */
              <Empty>
                {!filtered ? (
                  <>
                    No figures for this period yet.{" "}
                    {/* **The next action, not only the state.** A figure arrives from a statement or a
                        spreadsheet; an empty register that says so and stops leaves the reader to
                        guess which screen does that. */}
                    <Link to="/evidence" search={keepSearch} className={styles.fixLink}>
                      Upload a statement
                    </Link>{" "}
                    or{" "}
                    <Link to="/bulk" search={keepSearch} className={styles.fixLink}>
                      bring readings in from a spreadsheet
                    </Link>
                    .
                  </>
                ) : (
                  <>
                    No records match these filters.{" "}
                    <button type="button" className={styles.fixLink} onClick={register.clearAll}>
                      Clear them
                    </button>
                  </>
                )}
              </Empty>
            ) : (
              <>
                <div className={cls(own.tableWrap)}>
                <table className={cls(own.table)}>
                  {/* **A table needs a name, and this one is read by people who cannot see the panel
                      it is in** (EXP-39). A screen reader announces "table, 6 columns, 41 rows" and
                      nothing about what the rows are; the panel's heading is a sibling, not a label.
                      The caption also carries the one fact that cannot be inferred from a row — which
                      Scope 2 basis priced it — because a figure announced without it is one a listener
                      cannot interpret and there is no column for it. */}
                  <caption className="visuallyHidden">
                    {scope === "" ? "Every figure" : `Scope ${scope} figures`} for{" "}
                    {shortDate(period.period_start)} to {shortDate(period.period_end)},
                    {" "}
                    {/* **"on the location basis", not "location-based"**, and the wording is load
                        bearing for a reason that has nothing to do with the reader: `ui-check` asserts
                        that the *visible* sentence below the cards states the basis, by searching the
                        screen's whole text for "location-based". A caption carrying the same phrase
                        would satisfy that check on its own, so removing the visible sentence would stop
                        failing it — a hidden string quietly standing in for a visible guarantee. */}
                    {/* Only where it means something. "Scope 2 priced on the location basis" read out
                        over a Scope 1 table is a sentence about figures that are not in it. */}
                    {scope === "" || scope === "2"
                      ? `Scope 2 priced on the ${screen.method} basis. `
                      : ""}
                    {count(figuresTotal)} selected
                    {hidden > 0 ? `, ${count(shown.length)} of them drawn so far` : ""}.
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Activity &amp; category</th>
                      {!hiddenCols.has("site") ? <th scope="col">Site / period</th> : null}
                      {!hiddenCols.has("quantity") ? <th scope="col" className={cls(own.right)}>Quantity</th> : null}
                      <th scope="col" className={cls(own.right)}>tCO₂e</th>
                      {!hiddenCols.has("record") ? <th scope="col">Record</th> : null}
                      {!hiddenCols.has("evidence") ? <th scope="col">Evidence</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((figure) => (
                      <Row
                        key={figure.id}
                        figure={figure}
                        onOpen={() => screen.openReceipt(figure.id)}
                        hidden={hiddenCols}
                      />
                    ))}
                  </tbody>
                </table>
                </div>

                {/* **How many there are, how many are drawn, and a way to draw the rest** (EXP-39).
                    This was a cap and nothing more: the sentence said 500 of 4,312 and told the
                    reader to narrow with a filter, because the rows past the cap could not be reached
                    at all. Honest, and resting on a fact this code cannot see — `GET /figures` orders
                    by tonnage descending, so the undrawn rows were the smallest, and an `ORDER BY`
                    changed on an endpoint nobody connected to this screen would have turned a stated
                    limit into a register hiding the row somebody came for.

                    So the limit is an **opening ceiling** now (`RENDER_CAP`, `register.expand`). Three
                    things are load-bearing in what it says, and the first two were already:

                    - the total, the record count and both exports are over **every** selected figure,
                      never over what is drawn;
                    - the undrawn rows are the smallest, which is what makes the opening view the
                      useful one rather than what makes it defensible;
                    - and the cost of drawing them all is stated **before** the press, because a
                      register that goes unresponsive on a click is a product a reader stops trusting
                      with the next one.

                    Rendered whenever the selection is larger than the opening cap — including once
                    everything is drawn, when it says so. A notice that vanished at the moment it had
                    good news would take focus with it and leave a reader unsure whether they had seen
                    everything, which is the question this whole block exists to answer. */}
                {figuresTotal > RENDER_CAP ? (
                  <p
                    className={cls(own.capped)}
                    data-testid="render-cap"
                    // The count itself, as an attribute. A gate asserting "there is nothing more to
                    // draw" by searching this prose would be searching a sentence that names the
                    // thing it is denying — the trap an honest receipt fell into once. A number is
                    // not a sentence.
                    data-hidden={hidden}
                    ref={notice}
                    tabIndex={-1}
                  >
                    {hidden > 0 ? (
                      <>
                        <strong>
                          Drawing {count(shown.length)} of{" "}
                          {count(figuresTotal)} figures.
                        </strong>{" "}
                        The {count(hidden)} not drawn yet are read from the server as you ask for them,
                        and every one of them is already counted in the {total === undefined ? "" : `${tonnes(total)} `}
                        tCO₂e total and carried in both exports; nothing is left out of either.{" "}
                        {hidden > RENDER_STEP
                          ? `Drawing all ${count(figuresTotal)} at once is slower to scroll and slower to search.`
                          : ""}
                      </>
                    ) : (
                      <strong>
                        All {count(figuresTotal)} figures in this selection are drawn.
                      </strong>
                    )}
                    {hidden > 0 && !register.ceilingReached ? (
                      <span className={cls(own.cappedMore)}>
                        {/* **The ceiling, not a delta** — see `expand`. And the label is the number
                            it will add, so the control says what it does rather than naming a
                            batch size the reader has to hold in their head. */}
                        <button
                          type="button"
                          className={styles.fixLink}
                          data-testid="render-cap-more"
                          onClick={() => {
                            register.expand(cap + RENDER_STEP);
                            notice.current?.focus();
                          }}
                        >
                          {hidden > RENDER_STEP
                            ? `Draw ${count(RENDER_STEP)} more`
                            : `Draw the remaining ${count(hidden)}`}
                        </button>
                        {/* Only when it is a different action. At 312 remaining, "draw 500 more" and
                            "draw all" do the same thing, and two controls with one effect is how a
                            reader learns to read neither. */}
                        {hidden > RENDER_STEP && figuresTotal <= REGISTER_DRAW_LIMIT ? (
                          <button
                            type="button"
                            className={styles.fixLink}
                            data-testid="render-cap-all"
                            onClick={() => {
                              register.expand(figuresTotal);
                              notice.current?.focus();
                            }}
                          >
                            Draw all {count(figuresTotal)}
                          </button>
                        ) : null}
                      </span>
                    ) : null}
                    {register.ceilingReached ? (
                      <span className={cls(own.cappedMore)} data-testid="register-ceiling">
                        The register draws at most {count(REGISTER_DRAW_LIMIT)} figures. Narrow it with a
                        filter or the search to reach the rest; both exports carry the whole selection.
                      </span>
                    ) : null}
                  </p>
                ) : null}
              </>
            )}

            <p className={styles.note}>
              {/* The licence travels with the figures it describes — a condition, not a footnote. */}
              {(inventory?.attributions ?? []).join(" ")}
            </p>
          </Panel>
          )}
        </RecordPanel>
      </div>
    </ScreenFrame>
  );
}

function Row({
  figure,
  onOpen,
  hidden,
}: {
  figure: Figure;
  onOpen: () => void;
  hidden: ReadonlySet<ColumnKey>;
}) {
  /** What this row is, in one phrase. **Both of a row's controls need it** (EXP-39).
   *
   * The two controls read "stationary" and "Record ›". Tabbing a table hears the control, not the
   * row it is in — so a register of forty figures offered forty buttons called "Record" and a
   * handful called "electricity", and the only thing distinguishing them was a column a listener
   * never reaches. A screen reader's own list-of-links view is where this is most obviously
   * broken: forty identical entries.
   *
   * Assembled once rather than written into each `aria-label`, because the two must not disagree
   * about which row they are in.
   */
  const where = `${figure.category.replace(/_/g, " ")} at ${figure.facility_name}, ${shortDate(
    figure.period_start,
  )} to ${shortDate(figure.period_end)}`;

  // No `className` on the row: `own.row` is not declared in `Inventory.module.css`, and `cls` was
  // hiding that rather than fixing it — a reference that applies nothing reads as styled. The rule
  // itself is not invented here, because how a register row should look is a design decision and
  // this one has none today.
  return (
    <tr>
      {/* `scope="row"` so every other cell is announced with this one as its heading. Without it a
          listener moving across the row hears six values and has to remember which row they are
          on — and `<th>` rather than `<td>` is what makes the association, not the attribute. */}
      <th scope="row" className={cls(own.rowHead)}>
        <button
          type="button"
          className={cls(own.link)}
          onClick={onOpen}
          aria-label={`Receipt for ${where}`}
        >
          {figure.category.replace(/_/g, " ")}
        </button>
        {/* **An explicit receipt control beside the category** (whole-product review 2026-11). The
            category text opens the receipt too, but nothing about plain text says so, and a reader
            looking for "where does this number come from" should not have to guess which word to
            press. Its name starts "Open the receipt", not "Receipt for", so a list of the register's
            "Receipt for ..." controls still has exactly one entry per row. */}
        <button
          type="button"
          className={cls(own.receiptButton)}
          onClick={onOpen}
          aria-label={`Open the receipt for ${where}`}
          data-testid="row-receipt"
        >
          Receipt
        </button>
        <span className={cls(own.sub)}>
          Scope {figure.scope}
          {figure.method ? ` · ${figure.method}-based` : ""}
        </span>
        <span className={own.grade} data-grade={figure.quality}>Grade {figure.quality}</span>
      </th>
      {!hidden.has("site") ? (
        <td>
          {figure.facility_name}
          <span className={cls(own.sub)}>
            {shortDate(figure.period_start)} – {shortDate(figure.period_end)}
          </span>
        </td>
      ) : null}
      {/* **The cells follow the headers.** They did not: the record link sat in the Quantity
          column and every number was one column left of its heading, so a reader comparing a
          quantity against a heading read it as a tonnage. */}
      {!hidden.has("quantity") ? (
        <td className={cls(own.right, "num")}>
          {/* Three places (walkthrough C4, G-Wcontrib-04): 90 ccf is 9.333 MMBtu, and a register that
              printed "9 MMBtu" beside a tonnage that used 9.333 was a row nobody could recompute,
              while one that printed 2,153.948529 beside 1,386.5 mixed the engine's scale with the
              bill's. `computedQuantity` groups the thousands, rounds once to three places and drops
              the zeros; the exact value is the hover. */}
          <span title={`Exactly ${factorValue(figure.quantity)}`}>{computedQuantity(figure.quantity)}</span>{" "}
          {unitLabel(figure.unit)}
        </td>
      ) : null}
      <td className={cls(own.right, "num")}>{tonnes(figure.kgco2e)}</td>
      {!hidden.has("record") ? <td>
        {/* Two ways out of a row, because they answer different questions. The receipt explains
            **this figure**; the record page holds the activity behind it — its revisions, its
            source document and the work attached to it. Collapsing them into one destination would
            mean one of the two questions had no answer.

            A `Link` carrying the whole search, not an `<a href>` with a hard-coded basis. The href
            reloaded the document — throwing away the query cache — and wrote `method=location`,
            so opening a record from a market-based view silently changed which inventory was being
            read. Carrying the search forward is also what lets the record page offer a way back to
            *this* filtered list (EXP-12). */}
        <Link
          className={cls(own.link)}
          to="/records/$recordId"
          params={{ recordId: figure.activity_record_id }}
          search={keepSearch}
          aria-label={`Record for ${where}`}
        >
          Record ›
        </Link>
      </td> : null}
      {!hidden.has("evidence") ? <td>
        {/* **Never a bare tick.** F10: "traceable" was a non-empty string, and the home screen
            reported every figure traceable beside an empty library. A broken chain says which
            link, because a reader told only that it is broken has nothing to do about it. */}
        <ChainDots figure={figure} />{" "}
        {figure.receipt_is_complete ? (
          <span className={cls(own.ok)}>Complete</span>
        ) : (
          <span className={cls(own.broken)}>
            Breaks at {figure.evidence_broken_at ?? "an unknown step"}
          </span>
        )}
      </td> : null}
    </tr>
  );
}

export function InventoryScreen() {
  return (
    <RequireSession>
      <RequirePeriod title="All emissions" crumb="every figure, by scope and period">
        {(screen, period) => <InventoryScreenBody screen={screen} period={period} />}
      </RequirePeriod>
    </RequireSession>
  );
}
